export const nodeWidth = 172;
export const nodeHeight = 36;
export const horizontalSpacing = 70; // Space between sibling nodes
export const verticalSpacing = 10; // Space between levels
export const animationDuration = 600; // Animation duration in milliseconds

export const getLayoutedElements = (nodes, edges) => {
  // Build tree structure: map nodeId -> children array
  const childrenMap = new Map();
  const parentMap = new Map();
  const nodeSet = new Set(nodes.map(n => n.id));
  
  // Initialize children map for all nodes
  nodes.forEach(node => {
    childrenMap.set(node.id, []);
  });
  
  // Build parent-child relationships
  edges.forEach(edge => {
    if (nodeSet.has(edge.source) && nodeSet.has(edge.target)) {
      const children = childrenMap.get(edge.source) || [];
      children.push(edge.target);
      childrenMap.set(edge.source, children);
      parentMap.set(edge.target, edge.source);
    }
  });
  
  // Find root nodes (nodes with no parent)
  const rootNodes = nodes.filter(node => !parentMap.has(node.id));
  
  const positions = new Map();
  const subtreeHeights = new Map(); // Cache for subtree heights
  
  // Calculate subtree height recursively (vertical space for children)
  const getSubtreeHeight = (nodeId) => {
    if (subtreeHeights.has(nodeId)) {
      return subtreeHeights.get(nodeId);
    }
    
    const children = childrenMap.get(nodeId) || [];
    const visibleChildren = children.filter(childId => nodeSet.has(childId));
    
    if (visibleChildren.length === 0) {
      // Leaf node
      subtreeHeights.set(nodeId, nodeHeight);
      return nodeHeight;
    }
    
    // Sum of children heights plus spacing
    let totalHeight = 0;
    visibleChildren.forEach((childId, index) => {
      if (index > 0) {
        totalHeight += verticalSpacing;
      }
      totalHeight += getSubtreeHeight(childId);
    });
    
    subtreeHeights.set(nodeId, totalHeight);
    return totalHeight;
  };
  
  // Calculate level (depth) for each node
  const levelMap = new Map();
  const calculateLevels = (nodeId, level) => {
    levelMap.set(nodeId, level);
    const children = childrenMap.get(nodeId) || [];
    const visibleChildren = children.filter(childId => nodeSet.has(childId));
    visibleChildren.forEach(childId => {
      calculateLevels(childId, level + 1);
    });
  };
  
  rootNodes.forEach(root => {
    calculateLevels(root.id, 0);
  });
  
  // Position nodes recursively
  const positionNode = (nodeId, y, level) => {
    const children = childrenMap.get(nodeId) || [];
    const visibleChildren = children.filter(childId => nodeSet.has(childId));
    
    if (visibleChildren.length === 0) {
      // Leaf node - position at y
      positions.set(nodeId, {
        x: level * (nodeWidth + horizontalSpacing),
        y: y
      });
      return y + nodeHeight;
    }
    
    // Position children first (vertically)
    let currentY = y;
    visibleChildren.forEach(childId => {
      const childHeight = getSubtreeHeight(childId);
      positionNode(childId, currentY, level + 1);
      currentY += childHeight + verticalSpacing;
    });
    
    // Center parent to the left of its children
    const firstChildY = positions.get(visibleChildren[0]).y;
    const lastChildY = positions.get(visibleChildren[visibleChildren.length - 1]).y;
    const parentY = (firstChildY + lastChildY) / 2;
    
    positions.set(nodeId, {
      x: level * (nodeWidth + horizontalSpacing),
      y: parentY
    });
    
    return currentY - verticalSpacing; // Return bottom edge
  };
  
  // Position all root nodes
  let currentY = 0;
  rootNodes.forEach((root, index) => {
    if (index > 0) {
      currentY += verticalSpacing;
    }
    const subtreeHeight = getSubtreeHeight(root.id);
    positionNode(root.id, currentY, 0);
    currentY += subtreeHeight;
  });
  
  // Center the entire tree around origin (vertically)
  if (positions.size > 0) {
    const allY = Array.from(positions.values()).map(p => p.y);
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);
    const centerOffset = -(minY + maxY) / 2;
    
    positions.forEach((pos, nodeId) => {
      positions.set(nodeId, {
        x: pos.x,
        y: pos.y + centerOffset
      });
    });
  }
  
  // Create new nodes with positions
  const newNodes = nodes.map((node) => {
    const pos = positions.get(node.id) || { x: 0, y: 0 };
    return {
      ...node,
      targetPosition: 'left',
      sourcePosition: 'right',
      position: {
        x: pos.x - nodeWidth / 2,
        y: pos.y - nodeHeight / 2,
      },
      style: {
        ...node.style,
        transition: `transform ${animationDuration}ms ease-out, left ${animationDuration}ms ease-out, top ${animationDuration}ms ease-out`,
      },
    };
  });

  const newEdges = edges.map((edge) => ({
    ...edge,
    type: 'step',
  }));

  return { nodes: newNodes, edges: newEdges };
};

